// Работен лист „Изписване" — общият редактор, преизползван от страниците за
// създаване (WriteoffCreatePage) и редакция (WriteoffEditPage). Подредба като
// листа на доставката: заглавие с „← Изписване", карта с полетата на документа
// (Header), лента с търсачката — продукти И рецепти — и „Целият списък"
// (Search + `useWriteoffCatalog`), картата „Продукти" (решетка от десктоп ширина,
// карти под нея) и футър със сумите и действията.
//
// Логиката на редовете е в `useWriteoffRowsActions` (наличността към датата и
// стойността на рецептите — `useWriteoffStock`); недостигът при запис — в
// `useWriteoffShortageFlow`; черновата на новото (браузър + сървър) — в
// `useWriteoffDraft`. Клавиатурата е общата за модула: Ctrl/Cmd+Enter записва
// (при недостигаща съставка на рецепта — първо пита какво да се поправи), Esc
// от решетката/полето — към търсачката, „Esc отникъде" — към началния екран
// (новото — тихо, черновата остава; редакцията пита при промени).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { App } from "antd";
import type { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../../hooks/useViewport";
import {
  useInventorySettings,
  useProducts,
  useRecipes,
  useStockBalances,
  useWarehouses,
} from "../../../../hooks/useInventory";
import type { CreateWriteoffPayload } from "../../../../api/inventory";
import { clampDecimals } from "../../components/qty";
import { pickableProducts } from "../../components/product/deletedProducts";
import { useGridNavigation } from "../../components/gridNavigation";
import { useWorksheetKeyboard } from "../../components/useWorksheetKeyboard";
import { useWorksheetSearch } from "../../components/useWorksheetSearch";
import { InventoryTheme } from "../../components/InventoryTheme";
import { DraftPresence } from "../../components/drafts/DraftWorksheetParts";
import { WriteoffWorksheetHeader } from "./WriteoffWorksheetHeader";
import { WriteoffWorksheetSearch } from "./WriteoffWorksheetSearch";
import { WriteoffProductsCard } from "./WriteoffProductsCard";
import { WriteoffWorksheetFooter } from "./WriteoffWorksheetFooter";
import { WriteoffKindBadge } from "./WriteoffKindBadge";
import { useWriteoffRowsActions } from "./useWriteoffRowsActions";
import { useWriteoffLeave } from "./useWriteoffLeave";
import { useWriteoffShortageFlow } from "./useWriteoffShortageFlow";
import { useWriteoffDraft, type WriteoffSheetDraft } from "./useWriteoffDraft";
import { useWriteoffCatalog } from "./useWriteoffCatalog";
import {
  AUTO_SCRAP_REASON,
  WRITEOFF_REASONS,
  WRITEOFF_COL_COUNT,
  parseDecimal,
  type WriteoffKind,
  type WriteoffSearchResult,
  type WriteoffWorksheetInitial,
} from "./writeoffWorksheetShared";

// Началният екран на операцията — „назад", отказ и успешен запис водят тук
const HOME_PATH = "/inventory/writeoff";

type Props = {
  mode: "new" | "edit";
  title: string;
  initial: WriteoffWorksheetInitial;
  saving: boolean;
  // `onSaved` — страницата го вика при успешен запис (черновата изчезва)
  onSave: (payload: CreateWriteoffPayload, onSaved: () => void) => void;
  // черновата на сървъра, от която тръгва новото изписване (от страницата)
  draft?: WriteoffSheetDraft;
  // Складът от конфигурацията — попълва се, когато пристигне (create режим)
  defaultWarehouseId?: number | null;
  // Регистрира пазач за незапазени промени в layout-а (Inventory.tsx), за да
  // могат sidebar стрелката/менюто да питат преди напускане. null при демонтаж.
  onRegisterLeaveGuard?: (guard: ((proceed: () => void) => void) | null) => void;
};

export const WriteoffWorksheet = ({
  mode,
  title,
  initial,
  saving,
  onSave,
  draft,
  defaultWarehouseId = null,
  onRegisterLeaveGuard,
}: Props) => {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { isDesktop, isNarrow } = useViewport();

  const { data: warehouses = [] } = useWarehouses();
  const { data: products = [] } = useProducts();
  const { data: recipes = [] } = useRecipes();
  const { data: settings } = useInventorySettings();
  // Себестойността (FIFO) е ВИНАГИ нето: при регистрирана по ДДС фирма
  // бруто = нето × (1+ставка/100); при НЕрегистрирана бруто = нето.
  const vatRegistered = settings?.vatRegistered ?? true;

  // ─── Състояние на документа ───────────────────────────────────────────────
  const [date, setDate] = useState<Dayjs | null>(initial.date);
  const [warehouseIdValue, setWarehouseIdValue] = useState<number | null>(initial.warehouseId);
  const [kind, setKind] = useState<WriteoffKind>(initial.kind);
  const [reason, setReason] = useState(initial.reason);
  const [comment, setComment] = useState(initial.comment);

  // Складът от конфигурацията пристига асинхронно → попълваме само празно поле
  useEffect(() => {
    if (warehouseIdValue == null && defaultWarehouseId != null) setWarehouseIdValue(defaultWarehouseId);
  }, [defaultWarehouseId, warehouseIdValue]);

  const productById = useMemo(() => new Map(products.map((p) => [p.id, p])), [products]);

  // Флаг „незапазени промени" (вдига се при всяка реална промяна — touch) и
  // черновата на новото изписване; състоянието се чете лениво (редовете идват
  // от hook-а по-долу)
  const writeoffDraft = useWriteoffDraft({
    mode,
    homePath: HOME_PATH,
    draft,
    sheet: () => ({
      date,
      warehouseId: warehouseIdValue,
      warehouseName: warehouses.find((warehouse) => warehouse.id === warehouseIdValue)?.name ?? null,
      kind,
      reason,
      comment,
      lines,
      totalGross,
    }),
  });
  const { dirtyRef, touch } = writeoffDraft;

  // ─── Клавиатурна навигация в решетката (общата за модула) ────────────────
  // Колони: 0 Количество/Порции · 1 Партида · 2 Бележка. Ключовете на редовете
  // се четат лениво — редовете идват от hook, който сам иска `focusQtyCell`.
  // Добавяне от търсачката прави ВИНАГИ нов ред: партидата е на ниво ред.
  const searchBox = useWorksheetSearch({
    getResultCount: () => searchResults.length,
    onPickIndex: (index) => pickFromSearch(searchResults[index]),
    focusFirst: () => focusFirst(0),
  });
  const rowKeysRef = useRef<number[]>([]);
  const { focusCell, focusFirst, focusSideways, onGridKey, isInGrid } = useGridNavigation({
    prefix: "wox",
    colCount: WRITEOFF_COL_COUNT,
    getRowKeys: () => rowKeysRef.current,
    onEscape: searchBox.focusSearch,
    onLeaveTop: searchBox.focusSearch,
    enterOpensCols: [1], // „Партида" отваря падащото си меню с Enter
  });
  // Фокус върху „Количество" на ред (след добавяне от търсачката)
  const focusQtyCell = useCallback((lineKey: number) => focusCell(lineKey, 0), [focusCell]);

  // Автоматичен брак от сторно в Маркиране (белег: запазената причина): видът и
  // причината са залостени, а проверката за наличност се пропуска (и на BE) —
  // записът е роден с разрешен минус и наличността му може да е отрицателна.
  const isAutoScrap = initial.kind === "scrap" && initial.reason === AUTO_SCRAP_REASON;

  // ─── Редовете (state + операции + as-of наличност + суми) ────────────────
  const {
    lines,
    asOfByProduct,
    shortages,
    recipeStockByKey,
    totalNet,
    totalGross,
    validLines,
    unitTotals,
    patchLine,
    setRecipePortions,
    addFromSearch,
    addCatalogLines,
    requestRemove,
    splitRecipeRow,
    requestRevert,
  } = useWriteoffRowsActions({
    initialLines: initial.lines,
    initialNextKey: initial.nextKey,
    warehouseId: warehouseIdValue,
    asOfDayKey: date ? date.format("YYYY-MM-DD") : null,
    excludeMovementId: initial.editedWriteoffId ?? null,
    skipStockCheck: isAutoScrap,
    vatRegistered,
    productById,
    onTouch: touch,
    focusQtyCell,
  });
  // Редът на редовете за ↑/↓ в решетката
  rowKeysRef.current = lines.map((line) => line.key);

  // ─── Търсачка и „Целият списък" (продукти + рецепти) ─────────────────────
  // Наличност за падащия списък — текущата (информативна); редът показва
  // авторитетната наличност КЪМ ИЗБРАНАТА ДАТА (as-of, от useWriteoffRowsActions).
  const { data: stockBalances = [] } = useStockBalances(warehouseIdValue);
  const balanceByProduct = useMemo(
    () => new Map(stockBalances.map((b) => [b.productId, b])),
    [stockBalances],
  );
  const productResult = useCallback(
    (productId: number): WriteoffSearchResult | null => {
      const product = productById.get(productId);
      if (!product) return null;
      return {
        kind: "product",
        id: product.id,
        name: product.name,
        unit: product.unit ?? null,
        available: balanceByProduct.get(product.id)?.quantity ?? 0,
        decimals: clampDecimals(product.decimalPlaces),
      };
    },
    [productById, balanceByProduct],
  );

  const { searchWords } = searchBox;
  const searchResults = useMemo<WriteoffSearchResult[]>(() => {
    if (!searchWords.length) return [];
    const matches = (name: string) => {
      const haystack = name.toLowerCase();
      return searchWords.every((word) => haystack.includes(word));
    };
    const productResults = pickableProducts(products)
      .filter((p) => matches(p.name))
      .sort((a, b) => a.name.localeCompare(b.name, "bg"))
      .slice(0, 30)
      .map((p) => productResult(p.id) as WriteoffSearchResult);
    const recipeResults = recipes
      .filter((r) => matches(r.name))
      .sort((a, b) => a.name.localeCompare(b.name, "bg"))
      .slice(0, 15)
      .map((r) => ({ kind: "recipe" as const, id: r.id, name: r.name }));
    return [...productResults, ...recipeResults].slice(0, 40);
  }, [products, recipes, searchWords, productResult]);

  const pickFromSearch = (result: WriteoffSearchResult) => {
    searchBox.setSearch("");
    addFromSearch(result);
  };

  // „Целият списък": продукти + рецепти, с количеството в реда (без дубликат)
  const { openCatalog, catalogModal } = useWriteoffCatalog({
    products,
    recipes,
    lines,
    balanceByProduct,
    productResult,
    addCatalogLines,
    onDismiss: searchBox.focusSearch,
  });

  // ─── Пазач за незапазени промени ─────────────────────────────────────────
  const requestLeave = useWriteoffLeave({
    mode,
    kind,
    lines,
    productById,
    dirtyRef,
    flushDraft: writeoffDraft.sync.flush,
    onRegisterLeaveGuard,
  });
  const leaveToHome = () => requestLeave(() => navigate(HOME_PATH));

  // ─── Валидация + запис ───────────────────────────────────────────────────
  // Бракуване без предефинирана причина → блокираме записа (изписването е със
  // свободна/празна причина).
  const reasonError =
    kind === "scrap" && !isAutoScrap && !WRITEOFF_REASONS.some((r) => r.value === reason);
  // Недостигът (продукти + съставки на рецепти, към датата): пречката назовава
  // кое, колко трябва и колко има; при рецепта Ctrl+Enter пита какво да се поправи
  const shortageFlow = useWriteoffShortageFlow({
    t,
    lines,
    shortages,
    recipeStockByKey,
    dateLabel: date ? date.format("DD.MM.YYYY") : "",
    warehouseName: warehouses.find((warehouse) => warehouse.id === warehouseIdValue)?.name ?? "",
    setRecipePortions,
    splitRecipeRow,
    focusCell,
  });

  // Всички пречки — първата е подсказката в долната лента и тостът при запис
  const computeIssues = (): string[] => {
    const issues: string[] = [];
    if (!warehouseIdValue)
      issues.push(
        t(warehouses.length > 0 ? "inventoryPage.errWarehouse" : "inventoryPage.errNoWarehouses"),
      );
    if (!date) issues.push(t("inventoryPage.errDate"));
    if (reasonError) issues.push(t("inventoryPage.errScrapReason"));
    if (validLines.length === 0) issues.push(t("inventoryPage.errNoLines"));
    if (shortageFlow.issue) issues.push(shortageFlow.issue);
    return issues;
  };
  const issues = computeIssues();

  const submit = () => {
    if (saving) return;
    if (issues.length > 0 || !warehouseIdValue || !date) {
      // единствената пречка е недостигаща съставка на рецепта → въпросът с ходовете
      if (issues[0] === shortageFlow.issue && shortageFlow.interceptSubmit()) return;
      message.error(issues[0]);
      return;
    }
    const payload: CreateWriteoffPayload = {
      warehouseId: warehouseIdValue,
      date: date.toISOString(),
      kind,
      reason: reason.trim() || null,
      comment: comment.trim() || null,
      lines: validLines.map((line) =>
        line.kind === "recipe"
          ? {
              kind: "recipe" as const,
              recipeId: line.recipeId as number,
              quantity: parseDecimal(line.quantity),
              mode: "fifo" as const,
              note: line.note.trim() || null,
            }
          : {
              kind: "product" as const,
              productId: line.productId as number,
              quantity: parseDecimal(line.quantity),
              mode: line.lotId ? ("lot" as const) : ("fifo" as const),
              lotId: line.lotId,
              // цената идва от партидата/FIFO (BE я смята), без ръчен override
              unitPrice: null,
              note: line.note.trim() || null,
            },
      ),
    };
    dirtyRef.current = false; // записът е поет от страницата → без питане
    onSave(payload, writeoffDraft.onSaved);
  };

  // ─── Клавиатура: Ctrl+Enter / Esc / ↓ / машина за писане ─────────────────
  // Отвореният confirm (премахване на ред, напускане) и „Целият списък" са
  // antd слоеве — общият пазач за overlay оставя клавишите на тях.
  useWorksheetKeyboard({
    isDesktop,
    searchInputRef: searchBox.inputRef,
    searchTextRef: searchBox.searchTextRef,
    setSearch: searchBox.setSearch,
    isInGrid,
    focusSearch: searchBox.focusSearch,
    focusFirst: () => focusFirst(0),
    onPrimary: submit,
    onLeave: leaveToHome,
  });

  // ─── Рендер ───────────────────────────────────────────────────────────────
  return (
    // ЕДНА височина за всички контроли (полета в хедъра, търсачка, редакторите
    // в редовете)
    <InventoryTheme controlHeight={40}>
      <div className="flex h-full flex-col overflow-hidden bg-canvas text-ink">
        <WriteoffWorksheetHeader
          band={{
            back: { label: t("inventoryPage.writeoff"), onClick: leaveToHome },
            titleAddon: <WriteoffKindBadge kind={kind} />,
            title,
            right: mode === "new" ? <DraftPresence lockLostBy={writeoffDraft.sync.lockLostBy} /> : null,
          }}
          date={date}
          onDateChange={(d) => {
            setDate(d);
            touch();
          }}
          warehouseId={warehouseIdValue}
          onWarehouseChange={(id) => {
            setWarehouseIdValue(id);
            touch();
          }}
          warehouses={warehouses}
          kind={kind}
          onKindChange={(value) => {
            setKind(value);
            touch();
          }}
          autoScrapLocked={isAutoScrap}
          reason={reason}
          reasonError={reasonError}
          onReasonChange={(value) => {
            setReason(value);
            touch();
          }}
          comment={comment}
          onCommentChange={(value) => {
            setComment(value);
            touch();
          }}
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto ${
            isNarrow ? "px-3 py-3" : "px-6 py-4"
          }`}
        >
          <WriteoffWorksheetSearch
            attachInputRef={(node) => {
              searchBox.inputRef.current = node;
            }}
            value={searchBox.search}
            onValueChange={searchBox.setSearch}
            onFocusChange={searchBox.setFocused}
            onInputKeyDown={searchBox.onInputKeyDown}
            dropdownOpen={searchBox.dropdownOpen}
            results={searchResults}
            searchWords={searchWords}
            highlightedIndex={searchBox.highlightedIndex}
            onHighlightChange={searchBox.setHighlightedIndex}
            onPick={pickFromSearch}
            onOpenCatalog={openCatalog}
            isNarrow={isNarrow}
          />

          <WriteoffProductsCard
            unitTotals={unitTotals}
            onGridKey={onGridKey}
            table={{
              lines,
              productById,
              asOfByProduct,
              shortages,
              recipeStockByKey,
              vatRegistered,
              isDesktop,
              onPatchLine: patchLine,
              onRequestRemove: requestRemove,
              onSplitRecipe: (key) => void splitRecipeRow(key),
              onFitRecipePortions: setRecipePortions,
              onRequestRevert: requestRevert,
              onSideExit: focusSideways,
              totalNet,
              totalGross,
            }}
          />
        </div>

        <WriteoffWorksheetFooter
          mode={mode}
          totalNet={totalNet}
          totalGross={totalGross}
          issue={issues[0] ?? null}
          hasValidLines={validLines.length > 0}
          saving={saving}
          draft={writeoffDraft.footer(t, productById)}
          onCancel={leaveToHome}
          onSave={submit}
        />

        {catalogModal}
        {shortageFlow.dialog}
      </div>
    </InventoryTheme>
  );
};

export default WriteoffWorksheet;
